import { MailCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { Field } from "../../admin/ui.tsx";
import { useSession } from "../../admin/session.ts";
import { requestPasswordReset, resetPassword, signUp } from "../../auth/auth-api.ts";
import { Button } from "../../components/Button.tsx";
import { useLocale } from "../../i18n/index.ts";
import { passwordLengthOk } from "@divespots/contracts";
import { useNext, useRegistrationOpen } from "../LoginPage.tsx";
import { AuthCard } from "./AuthCard.tsx";
import { landingFor, nextSearch } from "./landing.ts";
import { failureText, looksLikeEmail } from "./messages.ts";
import { PasswordField } from "./PasswordField.tsx";
import { useNoReferrer } from "./useNoReferrer.ts";
import { SignInForm } from "./SignInForm.tsx";

const ErrorLine = ({ text }: { text: string | null }) =>
  text ? (
    <p className="adm-error m-0" role="alert">
      {text}
    </p>
  ) : null;

/** Signed-in visitors have no business here: on to where they were going. */
function useSignedInRedirect(): string | null {
  const session = useSession();
  const next = useNext();
  const locale = useLocale();
  return session.status === "user" ? landingFor(session.user, next, locale) : null;
}

/** `/:lang/login/registrieren`: name, email, password. Ends on "Prüfe dein Postfach", whoever the address is. */
export function SignUpPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const next = useNext();
  const reg = useRegistrationOpen();
  const redirect = useSignedInRedirect();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  if (redirect) return <Navigate to={redirect} replace />;
  const back = `/${locale}/login${nextSearch(next)}`;

  if (sentTo) {
    return (
      <AuthCard title={t("diver.signup.checkTitle")} status>
        <MailCheck aria-hidden="true" size={32} strokeWidth={1.75} className="text-ocean" />
        <p className="ds-text-body m-0">{t("diver.signup.checkText", { email: sentTo })}</p>
        <p className="ds-text-footnote text-label-secondary m-0">{t("diver.signup.checkNote")}</p>
        <div className="adm-actions">
          <Button variant="tinted" onClick={() => setSentTo(null)}>
            {t("diver.signup.otherEmail")}
          </Button>
          <Link className="ds-btn ds-btn--plain" to={back}>
            {t("diver.signup.toSignIn")}
          </Link>
        </div>
      </AuthCard>
    );
  }

  const submit = async () => {
    setError(null);
    if (!name.trim()) return setError(t("diver.signup.nameMissing"));
    if (!looksLikeEmail(email)) return setError(t("diver.login.badEmail"));
    if (!passwordLengthOk(password)) return setError(t("diver.auth.lengthInvalid"));
    setBusy(true);
    const res = await signUp({ name: name.trim(), email: email.trim(), password }, locale);
    setBusy(false);
    if (!res.ok) return setError(failureText(t, res));
    setSentTo(email.trim());
  };

  if (reg.known && !reg.open) {
    return (
      <AuthCard title={t("diver.signup.title")}>
        <p className="adm-warning" role="status" data-testid="registration-closed">
          {t("diver.login.registrationClosed")}
        </p>
        <div className="adm-actions">
          <Link className="ds-btn ds-btn--tinted" to={back}>
            {t("diver.signup.toSignIn")}
          </Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t("diver.signup.title")} lead={t("diver.signup.lead")}>
      <form
        className="grid gap-3"
        noValidate
        aria-label={t("diver.signup.title")}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label={t("diver.signup.name")} hint={t("diver.signup.nameHint")}>
          {({ id, describedBy }) => (
            <input
              id={id}
              name="name"
              className="adm-input"
              autoComplete="name"
              maxLength={120}
              aria-describedby={describedBy}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label={t("diver.login.email")}>
          {({ id }) => (
            <input
              id={id}
              name="email"
              className="adm-input"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        <PasswordField
          label={t("diver.login.password")}
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          lengthCheck
        />
        <ErrorLine text={error} />
        <Button type="submit" variant="filled" disabled={busy}>
          {t("diver.signup.submit")}
        </Button>
        <p className="login-links ds-text-subheadline m-0">
          <Link className="ds-btn ds-btn--plain" to={back}>
            {t("diver.signup.toSignIn")}
          </Link>
        </p>
      </form>
    </AuthCard>
  );
}

/** `/:lang/login/bestaetigt`: the confirmation link only verifies; the visitor signs in now. */
export function VerifiedPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const next = useNext();
  const reg = useRegistrationOpen();
  const redirect = useSignedInRedirect();
  const [params] = useSearchParams();
  const [landed, setLanded] = useState<string | null>(null);
  useNoReferrer();
  if (redirect || landed) return <Navigate to={landed ?? redirect ?? "/"} replace />;
  // /verify-email sends a bad link back with ?error=TOKEN_EXPIRED|INVALID_TOKEN|USER_NOT_FOUND:
  // never claim a confirmation that did not happen. Signing in with the right password sends a new link.
  const failed = params.get("error") !== null;
  return (
    <AuthCard
      title={failed ? t("diver.verified.errorTitle") : t("diver.verified.title")}
      lead={failed ? t("diver.verified.errorLead") : t("diver.verified.lead")}
    >
      <SignInForm
        locale={locale}
        search={nextSearch(next)}
        registrationOpen={reg.open}
        onSignedIn={(user) => setLanded(landingFor(user, next, locale))}
      />
    </AuthCard>
  );
}

/** `/:lang/login/passwort-vergessen`: always the same answer; staff are told an admin resets them. */
export function ResetRequestPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const next = useNext();
  const redirect = useSignedInRedirect();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  if (redirect) return <Navigate to={redirect} replace />;
  const back = `/${locale}/login${nextSearch(next)}`;

  if (sent) {
    return (
      <AuthCard title={t("diver.reset.sentTitle")} status>
        <MailCheck aria-hidden="true" size={32} strokeWidth={1.75} className="text-ocean" />
        <p className="ds-text-body m-0">{t("diver.reset.sentText")}</p>
        <p className="ds-text-footnote text-label-secondary m-0">{t("diver.reset.staffNote")}</p>
        <div className="adm-actions">
          <Link className="ds-btn ds-btn--tinted" to={back}>
            {t("diver.reset.toSignIn")}
          </Link>
        </div>
      </AuthCard>
    );
  }

  const submit = async () => {
    setError(null);
    if (!looksLikeEmail(email)) return setError(t("diver.login.badEmail"));
    setBusy(true);
    const res = await requestPasswordReset(email.trim(), locale);
    setBusy(false);
    if (!res.ok) return setError(failureText(t, res));
    setSent(true);
  };

  return (
    <AuthCard title={t("diver.reset.requestTitle")} lead={t("diver.reset.requestLead")}>
      <form
        className="grid gap-3"
        noValidate
        aria-label={t("diver.reset.requestTitle")}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label={t("diver.login.email")} hint={t("diver.reset.staffNote")}>
          {({ id, describedBy }) => (
            <input
              id={id}
              name="email"
              className="adm-input"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              aria-describedby={describedBy}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        <ErrorLine text={error} />
        <Button type="submit" variant="filled" disabled={busy}>
          {t("diver.reset.requestSubmit")}
        </Button>
        <p className="login-links ds-text-subheadline m-0">
          <Link className="ds-btn ds-btn--plain" to={back}>
            {t("diver.reset.toSignIn")}
          </Link>
        </p>
      </form>
    </AuthCard>
  );
}

/** `/:lang/login/neues-passwort?token=…`: the new password twice; it never signs in. */
export function ResetSetPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // read once and kept in state; the address bar and the Referer never carry it afterwards
  const [token] = useState(() => params.get("token") ?? "");
  useNoReferrer();
  useEffect(() => {
    if (!params.has("token")) return;
    window.history.replaceState(window.history.state, "", window.location.pathname);
    // once, on arrival
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [badToken, setBadToken] = useState(!token || params.get("error") !== null);
  const login = `/${locale}/login`;

  if (done) {
    return (
      <AuthCard title={t("diver.reset.doneTitle")} status>
        <p className="ds-text-body m-0">{t("diver.reset.doneText")}</p>
        <div className="adm-actions">
          <Button variant="filled" onClick={() => void navigate(login)}>
            {t("diver.reset.toSignIn")}
          </Button>
        </div>
      </AuthCard>
    );
  }

  if (badToken) {
    return (
      <AuthCard title={t("diver.reset.invalidTitle")}>
        <p className="adm-error m-0" role="alert">
          {t("diver.reset.invalidText")}
        </p>
        <div className="adm-actions">
          <Link className="ds-btn ds-btn--filled" to={`${login}/passwort-vergessen`}>
            {t("diver.reset.invalidAction")}
          </Link>
        </div>
      </AuthCard>
    );
  }

  const submit = async () => {
    setError(null);
    if (!passwordLengthOk(password)) return setError(t("diver.auth.lengthInvalid"));
    if (password !== repeat) return setError(t("diver.auth.mismatch"));
    setBusy(true);
    const res = await resetPassword(token, password);
    setBusy(false);
    if (res.ok) return setDone(true);
    if (res.reason === "invalid_token") return setBadToken(true);
    setError(failureText(t, res));
  };

  return (
    <AuthCard title={t("diver.reset.setTitle")} lead={t("diver.reset.setLead")}>
      <form
        className="grid gap-3"
        noValidate
        aria-label={t("diver.reset.setTitle")}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <PasswordField
          label={t("diver.auth.newPassword")}
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          lengthCheck
        />
        <PasswordField
          label={t("diver.auth.repeatPassword")}
          autoComplete="new-password"
          name="new-password-repeat"
          value={repeat}
          onChange={setRepeat}
        />
        <ErrorLine text={error} />
        <Button type="submit" variant="filled" disabled={busy}>
          {t("diver.reset.setSubmit")}
        </Button>
      </form>
    </AuthCard>
  );
}
